import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ShieldCheck, Search, LogOut, Building2, Users, ChevronDown, ChevronRight,
  Lock, Unlock, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const TOKEN_KEY = 'superadminToken';

// Alohida axios instance: global 401 interceptor (oddiy foydalanuvchini logout qiladi)
// va default Authorization header bu yerga ta'sir qilmasin.
const api = axios.create({ baseURL: `${API}/superadmin` });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const fmtDate = (d) => (d ? new Date(d).toLocaleString('uz-UZ', { dateStyle: 'short', timeStyle: 'short' }) : '—');

function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Faol
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Bloklangan
    </span>
  );
}

function BlockButton({ active, busy, onClick, size = 'md' }) {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${pad} ${
        active
          ? 'bg-red-50 text-red-700 hover:bg-red-100'
          : 'bg-green-50 text-green-700 hover:bg-green-100'
      }`}
    >
      {active ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
      {active ? 'Bloklash' : 'Blokdan chiqarish'}
    </button>
  );
}

function LoginForm({ onSuccess }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/login', { login, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Xato yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-surface-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-ink-secondary" />
          </div>
          <h1 className="font-bold text-lg text-ink">Superadmin</h1>
        </div>
        <input
          className="w-full px-3 py-2.5 border border-surface-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Login"
          autoComplete="username"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          required
        />
        <input
          type="password"
          className="w-full px-3 py-2.5 border border-surface-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Parol"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-60"
        >
          {loading ? 'Kirilmoqda…' : 'Kirish'}
        </button>
      </form>
    </div>
  );
}

function OrgUsers({ orgId, orgActive }) {
  const [users, setUsers] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get(`/organizations/${orgId}/users`)
      .then(({ data }) => setUsers(data.data))
      .catch((err) => toast.error(err.response?.data?.message || 'Xato yuz berdi'));
  }, [orgId]);

  const toggle = async (u) => {
    const next = !u.isActive;
    if (!next && !window.confirm(`"${u.name}" foydalanuvchisini bloklaysizmi?`)) return;
    setBusyId(u._id);
    try {
      await api.patch(`/users/${u._id}/status`, { isActive: next });
      setUsers((list) => list.map((x) => (x._id === u._id ? { ...x, isActive: next } : x)));
      toast.success(next ? 'Blokdan chiqarildi' : 'Bloklandi');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Xato yuz berdi');
    } finally {
      setBusyId(null);
    }
  };

  if (!users) return <div className="px-4 py-3 text-sm text-ink-tertiary">Yuklanmoqda…</div>;
  if (!users.length) return <div className="px-4 py-3 text-sm text-ink-tertiary">Foydalanuvchilar yo'q</div>;

  return (
    <div className="divide-y divide-surface-100">
      {!orgActive && (
        <div className="px-4 py-2 text-xs text-red-700 bg-red-50">
          Tashkilot bloklangan — hech bir xodim kira olmaydi.
        </div>
      )}
      {users.map((u) => (
        <div key={u._id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-ink truncate">{u.name}</div>
            <div className="text-xs text-ink-tertiary">{u.phone}{u.email ? ` · ${u.email}` : ''}</div>
          </div>
          <span className="text-xs text-ink-secondary capitalize w-16">{u.role}</span>
          <span className="text-xs text-ink-tertiary w-32">{fmtDate(u.lastLogin)}</span>
          <StatusBadge active={u.isActive} />
          <BlockButton size="sm" active={u.isActive} busy={busyId === u._id} onClick={() => toggle(u)} />
        </div>
      ))}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/organizations');
      setOrgs(data.data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(err.response?.data?.message || 'Xato yuz berdi');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      if (filter === 'active' && !o.isActive) return false;
      if (filter === 'blocked' && o.isActive) return false;
      if (!q) return true;
      return [o.name, o.slug, o.phone, o.email, o.owner?.name, o.owner?.phone]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [orgs, search, filter]);

  const stats = useMemo(() => ({
    total: orgs.length,
    active: orgs.filter((o) => o.isActive).length,
    blocked: orgs.filter((o) => !o.isActive).length,
    users: orgs.reduce((s, o) => s + o.usersTotal, 0),
  }), [orgs]);

  const toggleOrg = async (o) => {
    const next = !o.isActive;
    if (!next && !window.confirm(`"${o.name}" tashkilotini bloklaysizmi? Barcha xodimlari tizimdan chiqariladi.`)) return;
    setBusyId(o._id);
    try {
      await api.patch(`/organizations/${o._id}/status`, { isActive: next });
      setOrgs((list) => list.map((x) => (x._id === o._id ? { ...x, isActive: next } : x)));
      toast.success(next ? 'Blokdan chiqarildi' : 'Bloklandi');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Xato yuz berdi');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <header className="bg-white border-b border-surface-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5 text-primary-600" />
            <span className="font-bold text-ink">Superadmin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-surface-100 text-ink-secondary" title="Yangilash">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-surface-100 text-sm text-ink-secondary">
              <LogOut className="w-4 h-4" /> Chiqish
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Tashkilotlar', stats.total],
            ['Faol', stats.active],
            ['Bloklangan', stats.blocked],
            ['Foydalanuvchilar', stats.users],
          ].map(([label, value]) => (
            <div key={label} className="bg-white border border-surface-200 rounded-xl px-4 py-3">
              <div className="text-xs text-ink-tertiary">{label}</div>
              <div className="text-2xl font-bold text-ink mt-0.5">{value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, slug, telefon, egasi bo'yicha qidirish"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex bg-white border border-surface-200 rounded-lg p-1 text-sm">
            {[['all', 'Hammasi'], ['active', 'Faol'], ['blocked', 'Bloklangan']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-md font-medium ${filter === k ? 'bg-surface-100 text-ink' : 'text-ink-tertiary hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-surface-200 rounded-xl overflow-hidden">
          {loading && !orgs.length ? (
            <div className="py-12 flex justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
            </div>
          ) : !visible.length ? (
            <div className="py-12 text-center text-sm text-ink-tertiary">Hech narsa topilmadi</div>
          ) : (
            <div className="divide-y divide-surface-200">
              {visible.map((o) => {
                const open = expanded === o._id;
                return (
                  <div key={o._id}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                      <button
                        onClick={() => setExpanded(open ? null : o._id)}
                        className="flex items-center gap-3 min-w-0 flex-1 text-left"
                      >
                        {open ? <ChevronDown className="w-4 h-4 text-ink-tertiary shrink-0" /> : <ChevronRight className="w-4 h-4 text-ink-tertiary shrink-0" />}
                        <div className="w-9 h-9 rounded-lg bg-surface-100 flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4 text-ink-secondary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-ink truncate">{o.name}</div>
                          <div className="text-xs text-ink-tertiary truncate">
                            {o.slug}{o.owner ? ` · ${o.owner.name} (${o.owner.phone})` : ''}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-1 text-xs text-ink-secondary w-16" title="Faol / jami foydalanuvchilar">
                        <Users className="w-3.5 h-3.5" /> {o.usersActive}/{o.usersTotal}
                      </div>
                      <div className="text-xs text-ink-tertiary w-36 hidden md:block" title="Oxirgi kirish">
                        {fmtDate(o.lastLogin)}
                      </div>
                      <div className="text-xs text-ink-tertiary w-24 hidden md:block" title="Ro'yxatdan o'tgan">
                        {o.createdAt ? new Date(o.createdAt).toLocaleDateString('uz-UZ') : '—'}
                      </div>
                      <StatusBadge active={o.isActive} />
                      <BlockButton active={o.isActive} busy={busyId === o._id} onClick={() => toggleOrg(o)} />
                    </div>
                    {open && (
                      <div className="bg-surface-50 border-t border-surface-200">
                        <OrgUsers orgId={o._id} orgActive={o.isActive} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function SuperAdminPage() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  return authed ? <Dashboard onLogout={logout} /> : <LoginForm onSuccess={() => setAuthed(true)} />;
}
